import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ownerApi, formatDetail } from "../api";
import { formatCTDate } from "../lib/time";
import { Loader2, Plus, Building2, Check, X } from "lucide-react";
import { toast } from "@/lib/toastBanner";

const TIER_LABEL = { level_1: "Level I", level_2: "Level II", level_3: "Level III" };
const STATUS_STYLE = {
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  suspended: "bg-[#10b7c0]/15 text-[#10b7c0] border-[#10b7c0]/30",
  archived: "bg-[#ebeef5]/10 text-[#ebeef5]/70 border-white/10",
  deleted: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function Organizations() {
  const [orgs, setOrgs] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await ownerApi.get(`/organizations?include_archived=${showArchived}`);
      setOrgs(data);
    } catch {
      setOrgs([]);
      toast.error("Failed to load organizations");
    }
  };

  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6" data-testid="owner-orgs">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#f1fcff]">Organizations</h1>
          <p className="text-sm text-[#ebeef5]/70 mt-1">Tenants on the platform.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#ebeef5]/70 flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="accent-[#10b7c0]"
              data-testid="owner-orgs-archived-toggle"
            />
            Show archived
          </label>
          <button
            onClick={() => setCreateOpen(true)}
            className="bg-[#10b7c0] hover:bg-[#0ea2aa] text-black text-xs font-semibold px-3 py-2 rounded-md flex items-center gap-1.5"
            data-testid="owner-orgs-create-btn"
          >
            <Plus className="h-3.5 w-3.5" /> New Organization
          </button>
        </div>
      </div>

      {orgs === null ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-[#10b7c0]" /></div>
      ) : orgs.length === 0 ? (
        <div className="bg-[#0a1530] border border-white/10 rounded-xl p-12 text-center">
          <Building2 className="h-8 w-8 text-[#ebeef5]/50 mx-auto mb-3" />
          <p className="text-sm text-[#ebeef5]/70">No organizations yet.</p>
          <p className="text-xs text-[#ebeef5]/50 mt-1">Create one to get started.</p>
        </div>
      ) : (
        <div className="bg-[#0a1530] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="owner-orgs-table">
            <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-[#ebeef5]/70">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Name</th>
                <th className="text-left px-5 py-3 font-semibold">Slug</th>
                <th className="text-left px-5 py-3 font-semibold">Tier</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Provisioned</th>
                <th className="text-left px-5 py-3 font-semibold">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {orgs.map((o) => (
                <tr key={o.id} className="hover:bg-white/[0.02]" data-testid={`owner-org-row-${o.id}`}>
                  <td className="px-5 py-3">
                    <Link to={`/owner/organizations/${o.id}`} className="font-semibold text-[#f1fcff] hover:text-[#10b7c0]">
                      {o.name}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-xs font-mono text-[#ebeef5]/70">{o.slug}</td>
                  <td className="px-5 py-3 text-xs text-[#ebeef5]">{TIER_LABEL[o.preset_tier] || o.preset_tier}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${STATUS_STYLE[o.status] || ""}`}>
                      {o.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs">
                    {o.pending_provisioning ? (
                      <span className="inline-flex items-center gap-1 text-[#10b7c0]"><X className="h-3 w-3" />Pending</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-emerald-400"><Check className="h-3 w-3" />Live</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-[#ebeef5]/50">{formatCTDate(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <CreateOrgDialog onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />
      )}
    </div>
  );
}

function CreateOrgDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", slug: "", preset_tier: "level_1",
    admin_email: "", admin_password: "", admin_first: "", admin_last: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
        preset_tier: form.preset_tier,
        first_admin: {
          email: form.admin_email.trim(),
          password: form.admin_password,
          first_name: form.admin_first,
          last_name: form.admin_last,
        },
      };
      await ownerApi.post("/organizations", payload);
      toast.success("Organization created");
      onCreated();
    } catch (e2) {
      setErr(formatDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0a1530] border border-white/10 rounded-xl p-6 w-full max-w-lg"
        data-testid="owner-orgs-create-dialog"
      >
        <h2 className="font-heading font-bold text-lg text-[#f1fcff] mb-1">New Organization</h2>
        <p className="text-xs text-[#ebeef5]/70 mb-5">Creates the org plus its first System Admin. The SysAdmin will be promoted into the org at migration cutover (Phase 4).</p>

        <Field label="Organization name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="org-name" />
        <Field label="Slug (optional)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="auto-generated from name" testid="org-slug" />
        <Select
          label="Feature tier"
          value={form.preset_tier}
          onChange={(v) => setForm({ ...form, preset_tier: v })}
          options={[
            ["level_1", "Level I — Facilitation"],
            ["level_2", "Level II — Curriculum"],
            ["level_3", "Level III — Research"],
          ]}
          testid="org-tier"
        />

        <div className="mt-5 pt-4 border-t border-white/5">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-[#10b7c0] mb-3">First System Admin</p>
          <Field label="Email" required type="email" value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} testid="admin-email" />
          <Field label="Temporary password" required type="password" value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} testid="admin-password" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" value={form.admin_first} onChange={(v) => setForm({ ...form, admin_first: v })} testid="admin-first" />
            <Field label="Last name" value={form.admin_last} onChange={(v) => setForm({ ...form, admin_last: v })} testid="admin-last" />
          </div>
        </div>

        {err && <p className="mt-3 text-xs text-red-400" data-testid="owner-orgs-create-error">{err}</p>}

        <div className="mt-6 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="text-xs font-semibold px-3 py-2 rounded-md border border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff]">Cancel</button>
          <button type="submit" disabled={busy} className="text-xs font-semibold px-3 py-2 rounded-md bg-[#10b7c0] text-black hover:bg-[#0ea2aa] disabled:opacity-50 flex items-center gap-1.5" data-testid="owner-orgs-create-submit">
            {busy && <Loader2 className="h-3 w-3 animate-spin" />}
            Create Organization
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, placeholder, testid }) {
  return (
    <div className="mb-3">
      <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/70 mb-1">
        {label}{required && <span className="text-red-400"> *</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-[#040b1d] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#f1fcff] font-body focus:outline-none focus:border-[#10b7c0]/50"
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div className="mb-3">
      <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/70 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-[#040b1d] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#f1fcff] font-body focus:outline-none focus:border-[#10b7c0]/50"
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
